import React from "react";
import { Activity, ArrowDown, ArrowUp, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

/**
 * Component to display churn metrics for a customer
 */
export default function ChurnMetricsCard({
  churnData,
  isLoading,
  customerCode,
}) {
  // Debug logging removed for production

  if (isLoading) {
    return (
      <Card className="bg-background shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <Activity className="w-4 h-4" />
            Churn Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid place-items-center py-2">
            <div className="text-sm text-muted-foreground animate-pulse">
              Loading churn data...
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!churnData) {
    return (
      <Card className="bg-background shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <Activity className="w-4 h-4" />
            Churn Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-2 text-muted-foreground text-sm">
            <AlertTriangle className="w-4 h-4 mr-1" />
            No churn data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Helper function to get color based on churn rank
  const getChurnRankColor = (churnRank) => {
    switch (churnRank) {
      case "above":
        return "bg-green-500/10 text-green-700 border-green-500/20"; // High performing - Green
      case "on-track":
        return "bg-blue-500/10 text-blue-700 border-blue-500/20"; // Normal patterns - Light Blue
      case "churn - green":
        return "bg-yellow-500/10 text-yellow-700 border-yellow-500/20"; // Light risk - Yellow
      case "churn - orange":
        return "bg-orange-500/10 text-orange-700 border-orange-500/20"; // Moderate risk - Orange
      case "churn - red":
        return "bg-red-500/10 text-red-700 border-red-500/20"; // Severe risk - Red
      default:
        return "bg-muted/50 text-muted-foreground"; // Default case
    }
  };

  // Helper function to format numbers
  const formatValue = (value) => {
    if (value === undefined || value === null) return "N/A";

    // Format currency values
    if (typeof value === "number" && Math.abs(value) > 100) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }

    // Format percentages and other numbers
    if (typeof value === "number") {
      return value.toFixed(2);
    }

    return value;
  };

  // Get absolute difference between actual and expected
  const orderCountDiff =
    churnData.order_count_last12m - churnData.expected_order_count;
  const orderValueDiff =
    churnData.order_value_sum_last12m - churnData.expected_order_value_sum;

  return (
    <Card className="bg-background shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <Activity className="w-4 h-4" />
          Churn Metrics
        </CardTitle>
        <div
          className={`px-2 py-1 text-xs rounded-full ${getChurnRankColor(churnData.churn_rank)}`}
        >
          {churnData.churn_rank
            ? churnData.churn_rank.replace(" - ", ": ")
            : "Unknown"}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {/* Order Count Metrics */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">Orders (12m)</div>
              <div className="text-sm font-medium flex items-center gap-1">
                {orderCountDiff > 0 ? (
                  <ArrowUp className="h-3 w-3 text-green-500" />
                ) : orderCountDiff < 0 ? (
                  <ArrowDown className="h-3 w-3 text-red-500" />
                ) : null}
                <span>{churnData.order_count_last12m || 0}</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground flex justify-between">
              <span>Expected:</span>
              <span>{churnData.expected_order_count?.toFixed(1) || "N/A"}</span>
            </div>
          </div>

          {/* Order Value Metrics */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">Value (12m)</div>
              <div className="text-sm font-medium flex items-center gap-1">
                {orderValueDiff > 0 ? (
                  <ArrowUp className="h-3 w-3 text-green-500" />
                ) : orderValueDiff < 0 ? (
                  <ArrowDown className="h-3 w-3 text-red-500" />
                ) : null}
                <span>{formatValue(churnData.order_value_sum_last12m)}</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground flex justify-between">
              <span>Expected:</span>
              <span>{formatValue(churnData.expected_order_value_sum)}</span>
            </div>
          </div>

          {/* Relative Difference Value */}
          <div className="col-span-2 mt-2 bg-muted/20 p-2 rounded-md border">
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                Relative Difference
              </div>
              <div
                className={`text-sm font-medium ${
                  churnData.relative_diff_value > 0
                    ? "text-green-600"
                    : churnData.relative_diff_value < 0
                      ? "text-red-600"
                      : ""
                }`}
              >
                {churnData.relative_diff_value !== undefined &&
                churnData.relative_diff_value !== null
                  ? `${(churnData.relative_diff_value * 100).toFixed(1)}%`
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
